import type { CommandItem } from '@sunucupark/ui/command';
import { useNavigate } from '@tanstack/react-router';
import { lazy, Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFolders } from '@/features/folders';
import { useLayout } from './state/layout';
import { usePreferences } from './state/preferences';
import { applyColorScheme } from './theme';

/** The palette is the only place cmdk is used, so it arrives when it is first opened. */
const CommandPalette = lazy(() =>
  import('@sunucupark/ui/command').then((module) => ({ default: module.CommandPalette })),
);

export interface CommandPaletteBridgeProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opens the keyboard help, which is a command of its own. */
  onShowShortcuts: () => void;
}

/**
 * Every command in one search box (⌘K / Ctrl+K): the folders of this mailbox,
 * the settings that can be flipped without leaving the keyboard, and the pages
 * one can go to. Search over mail joins it in phase 2c.
 */
export function CommandPaletteBridge({ open, onOpenChange, onShowShortcuts }: CommandPaletteBridgeProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const folders = useFolders();
  const setDensity = useLayout((state) => state.setDensity);
  const setReadingPane = useLayout((state) => state.setReadingPane);
  const setColorScheme = usePreferences((state) => state.setColorScheme);
  const items = useMemo<CommandItem[]>(() => {
    const folderCommands = (folders.data ?? []).map<CommandItem>((folder) => ({
      id: `folder-${folder.id}`,
      label: folder.systemKind ? t(`folders.${folder.systemKind}`) : folder.name,
      icon: 'folder',
      group: t('palette.groups.folders'),
      keywords: [folder.name],
      onSelect: () =>
        void navigate({ to: '/mail/$folderId', params: { folderId: folder.id }, search: { view: 'conversation' } }),
    }));

    const theme = (scheme: 'light' | 'dark' | 'system'): CommandItem => ({
      id: `theme-${scheme}`,
      label: t('palette.theme', { theme: t(`settings.themes.${scheme}`) }),
      icon: 'settings',
      group: t('palette.groups.appearance'),
      onSelect: () => {
        setColorScheme(scheme);
        applyColorScheme(scheme);
      },
    });

    return [
      ...folderCommands,
      theme('light'),
      theme('dark'),
      theme('system'),
      ...(['compact', 'default', 'comfortable'] as const).map<CommandItem>((step) => ({
        id: `density-${step}`,
        label: t('palette.density', { density: t(`view.densityStep.${step}`) }),
        icon: 'settings',
        group: t('palette.groups.appearance'),
        onSelect: () => setDensity(step),
      })),
      ...(['right', 'bottom', 'off'] as const).map<CommandItem>((pane) => ({
        id: `pane-${pane}`,
        label: t('palette.readingPane', { pane: t(`view.pane.${pane}`) }),
        icon: 'settings',
        group: t('palette.groups.appearance'),
        onSelect: () => setReadingPane(pane),
      })),
      {
        id: 'open-settings',
        label: t('settings.title'),
        icon: 'settings',
        group: t('palette.groups.go'),
        onSelect: () => void navigate({ to: '/settings' }),
      },
      {
        id: 'show-shortcuts',
        label: t('shortcuts.title'),
        icon: 'help',
        shortcut: '?',
        group: t('palette.groups.go'),
        onSelect: onShowShortcuts,
      },
    ];
  }, [folders.data, navigate, onShowShortcuts, setColorScheme, setDensity, setReadingPane, t]);

  if (!open) return null;
  return (
    <Suspense fallback={null}>
      <CommandPalette
        open={open}
        onOpenChange={onOpenChange}
        items={items}
        label={t('palette.label')}
        description={t('palette.description')}
        placeholder={t('palette.placeholder')}
        emptyMessage={t('palette.empty')}
      />
    </Suspense>
  );
}
